import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../supabase'
import BDAddressFields from '../components/BDAddressFields'
import PhoneInput from '../components/PhoneInput'

// ── Brand tokens — standalone, no CSS vars (public-facing page) ──
const NAVY   = '#0B1F3A'
const TEAL   = '#2DD4B0'
const TEAL_D = '#1BA888'   // darker teal for hover
const WARM   = '#F7F9FC'
const AMBER  = '#F59E0B'

const TIMER_TOTAL = 7 * 60

const GENDER_OPTIONS = ['Prefer not to say', 'Female', 'Male', 'Other']
const AGE_GROUPS     = ['Prefer not to say', 'Under 18', '18-24', '25-34', '35-44', '45-54', '55-64', '65+']

// ── Shared input style — 16px prevents iOS zoom ──
const iStyle = {
  width: '100%', border: '1.5px solid #E2E8F0', borderRadius: '10px',
  padding: '11px 14px', fontSize: '16px', outline: 'none',
  background: '#fff', color: '#0F172A', fontFamily: 'inherit',
  boxSizing: 'border-box', transition: 'border-color 0.15s',
  WebkitAppearance: 'none',
}
const iF = (e) => { e.target.style.borderColor = TEAL }
const iB = (e) => { e.target.style.borderColor = '#E2E8F0' }

// ── Label ──
function Lbl({ children, optional, hint }) {
  return (
    <div style={{ marginBottom: '6px' }}>
      <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block' }}>
        {children}
        {optional && <span style={{ fontWeight: 400, color: '#9CA3AF', marginLeft: '5px', fontSize: '12px' }}>optional</span>}
      </label>
      {hint && <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px', lineHeight: 1.5 }}>{hint}</div>}
    </div>
  )
}

// ── Section card ──
function SCard({ title, accent, children }) {
  return (
    <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      {title && (
        <div style={{ padding: '10px 18px', borderBottom: '1px solid #F3F4F6', background: '#FAFAFA', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {accent && <div style={{ width: '3px', height: '14px', borderRadius: '2px', background: accent, flexShrink: 0 }} />}
          <span style={{ fontSize: '11px', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</span>
        </div>
      )}
      <div style={{ padding: '16px 18px' }}>{children}</div>
    </div>
  )
}

function CustomerOrderForm() {
  const { token } = useParams()

  const [loading, setLoading]       = useState(true)
  const [request, setRequest]       = useState(null)
  const [seller, setSeller]         = useState(null)
  const [expired, setExpired]       = useState(false)
  const [submitted, setSubmitted]   = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState('')
  const [timeLeft, setTimeLeft]     = useState(TIMER_TOTAL)
  const [timerStarted, setTimerStarted] = useState(false)
  const [showProfile, setShowProfile]   = useState(false)
  const timerRef = useRef(null)

  const [form, setForm] = useState({
    customer_name: '', country_code: '+880', phone: '', product_code: '', notes: '',
    house: '', road: '', moholla: '', extra: '',
    thana: '', thana_id: '', district: '', district_id: '',
    division: '', division_id: '', union_name: '', union_id: '',
    zip: '', country: 'Bangladesh',
    metro_thana: '', metro_thana_id: '', neighbourhood: '', neighbourhood_id: '',
    birthday: '', anniversary: '', gender: '', age_range: '',
    whatsapp: '', email: '', facebook_url: '', instagram_url: '',
  })

  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }))

  useEffect(() => {
    if (token) fetchRequest()
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [token]) // eslint-disable-line

  const fetchRequest = async () => {
    setLoading(true)
    const { data, error: fetchError } = await supabase
      .from('customer_order_requests').select('*').eq('token', token).single()

    if (fetchError || !data) { setExpired(true); setLoading(false); return }

    const { data: sellerData } = await supabase
      .from('sellers').select('shop_name, seller_mode, seller_currency, phone').eq('id', data.seller_id).single()

    const { data: brandSettings } = await supabase
      .from('admin_settings').select('key, value').in('key', ['brand_logo_light_url', 'brand_name'])

    const brandMap = {}
    brandSettings?.forEach(s => { brandMap[s.key] = s.value })

    const sellerFull = { ...sellerData, logo_light_url: brandMap.brand_logo_light_url || '', brand_name: brandMap.brand_name || 'PageConnect' }

    if (['submitted','approved','declined'].includes(data.status)) {
      setSubmitted(true); setRequest(data); setSeller(sellerFull); setLoading(false); return
    }

    const expiresAt = new Date(data.expires_at)
    if (new Date() > expiresAt) {
      setExpired(true); setRequest(data); setSeller(sellerFull); setLoading(false); return
    }

    const secondsLeft = Math.floor((expiresAt - new Date()) / 1000)
    setTimeLeft(Math.min(secondsLeft, TIMER_TOTAL))
    setRequest(data); setSeller(sellerFull)
    if (data.product_code) setF('product_code', data.product_code)
    if (sellerData?.seller_mode === 'international') { setF('country', ''); setF('country_code', '+61') }
    setLoading(false)
    startTimer(secondsLeft)
  }

  const startTimer = (seconds) => {
    setTimerStarted(true)
    let remaining = seconds
    timerRef.current = setInterval(() => {
      remaining -= 1; setTimeLeft(remaining)
      if (remaining <= 0) { clearInterval(timerRef.current); setExpired(true) }
    }, 1000)
  }

  const handleSubmit = async () => {
    if (!form.customer_name || !form.phone) { setError('Please enter your name and phone number'); return }
    if (!form.district) { setError('Please fill in your delivery address'); return }
    setSubmitting(true); setError('')

    const fullPhone = form.country_code + form.phone
    const oneMinuteAgo = new Date(Date.now() - 60 * 1000).toISOString()
    const { data: recent } = await supabase.from('customer_order_requests')
      .select('id').eq('seller_id', request.seller_id).eq('customer_phone', fullPhone)
      .eq('status', 'submitted').gte('submitted_at', oneMinuteAgo)

    if (recent && recent.length > 0) {
      setError('Please wait 1 minute before submitting again'); setSubmitting(false); return
    }

    const { error: updateError } = await supabase.from('customer_order_requests').update({
      customer_name: form.customer_name, customer_country_code: form.country_code,
      customer_phone: fullPhone, product_code: form.product_code || null,
      house: form.house, road: form.road, moholla: form.moholla,
      thana: form.thana !== '__na__' && form.thana !== '__other__' ? form.thana : null,
      thana_id: form.thana_id || null, district: form.district, district_id: form.district_id || null,
      division: form.division, division_id: form.division_id || null,
      union_name: form.union_name || null, union_id: form.union_id || null,
      zip: form.zip || null, country: form.country, extra: form.extra || null,
      metro_thana: form.metro_thana || null, neighbourhood: form.neighbourhood || null,
      notes: form.notes || null, birthday: form.birthday || null,
      anniversary: form.anniversary || null, gender: form.gender || null,
      age_range: form.age_range || null, whatsapp: form.whatsapp || null,
      email: form.email || null, facebook_url: form.facebook_url || null,
      instagram_url: form.instagram_url || null,
      status: 'submitted', submitted_at: new Date().toISOString(),
    }).eq('token', token)

    if (updateError) { setError('Something went wrong — please try again'); setSubmitting(false); return }
    clearInterval(timerRef.current)
    setSubmitted(true); setSubmitting(false)
  }

  const courierPreview = () => {
    return [
      form.house && form.house !== 'N/A' ? 'House: ' + form.house : null,
      form.moholla && form.moholla !== 'N/A' ? form.moholla : null,
      form.road && form.road !== 'N/A' ? 'Road: ' + form.road : null,
      form.neighbourhood && form.neighbourhood !== 'N/A' ? form.neighbourhood : null,
      form.metro_thana && form.metro_thana !== 'N/A' ? form.metro_thana : null,
      form.union_name && !['N/A','__na__'].includes(form.union_name) ? form.union_name : null,
      form.zip || null,
      form.thana && !['N/A','__na__','__other__'].includes(form.thana) ? form.thana : null,
      form.district && form.district !== 'N/A' ? form.district : null,
      form.division && !['N/A','__na__'].includes(form.division) ? form.division : null,
      form.country || null,
    ].filter(Boolean).join(', ')
  }

  const isInt      = seller?.seller_mode === 'international'
  const mins       = Math.floor(timeLeft / 60)
  const secs       = timeLeft % 60
  const timerUrgency = timeLeft > 120 ? 'ok' : timeLeft > 60 ? 'warn' : 'crit'
  const timerColors  = { ok: { text:'#16A34A', bg:'#F0FDF4', border:'#A7F3D0' }, warn: { text: AMBER, bg:'#FFFBEB', border:'#FDE68A' }, crit: { text:'#DC2626', bg:'#FEF2F2', border:'#FECACA' } }
  const TC = timerColors[timerUrgency]

  const PROFILE_FIELDS = ['birthday','anniversary','gender','age_range','whatsapp','email','facebook_url','instagram_url']
  const filledProfile  = PROFILE_FIELDS.filter(k => form[k]).length

  const waLink = seller?.phone ? 'https://wa.me/' + seller.phone.replace(/\D/g,'') : null

  // ── LOADING ──
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: WARM }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', border: '3px solid ' + TEAL, borderTopColor: 'transparent', animation: 'cof-spin 0.7s linear infinite', margin: '0 auto 12px' }} />
          <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Loading order form...</div>
        </div>
        <style>{`@keyframes cof-spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    )
  }

  // ── EXPIRED ──
  if (expired) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: NAVY }}>
        <div style={{ maxWidth: '360px', width: '100%', textAlign: 'center' }}>
          {seller?.logo_light_url
            ? <img src={seller.logo_light_url} alt={seller?.shop_name} style={{ height: '36px', objectFit: 'contain', margin: '0 auto 24px', display: 'block' }} />
            : <div style={{ fontSize: '20px', fontWeight: 700, color: '#fff', marginBottom: '24px', fontFamily: 'Space Grotesk, sans-serif' }}>{seller?.shop_name || 'PageConnect'}</div>
          }
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(239,68,68,0.15)', border: '2px solid rgba(239,68,68,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>This link has expired</div>
          <div style={{ fontSize: '13px', color: '#6B7A90', marginBottom: '20px', lineHeight: 1.6 }}>
            Order form links are valid for 7 minutes only to keep your order secure.
          </div>
          <div style={{ padding: '14px 16px', borderRadius: '12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '13px', color: '#D1D5DB', lineHeight: 1.6, marginBottom: '16px' }}>
            Please contact <strong style={{ color: '#fff' }}>{seller?.shop_name || 'the seller'}</strong> and ask for a new order form link.
          </div>
          {waLink && (
            <a href={waLink} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '13px', borderRadius: '12px', background: '#25D366', border: 'none', fontSize: '14px', fontWeight: 700, color: '#fff', textDecoration: 'none' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Message {seller?.shop_name || 'seller'} on WhatsApp
            </a>
          )}
        </div>
        <style>{`@keyframes cof-spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    )
  }

  // ── SUBMITTED ──
  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: NAVY }}>
        <div style={{ maxWidth: '380px', width: '100%', textAlign: 'center' }}>
          {seller?.logo_light_url
            ? <img src={seller.logo_light_url} alt={seller?.shop_name} style={{ height: '36px', objectFit: 'contain', margin: '0 auto 24px', display: 'block' }} />
            : <div style={{ fontSize: '20px', fontWeight: 700, color: '#fff', marginBottom: '24px' }}>{seller?.shop_name || 'PageConnect'}</div>
          }

          {/* Success icon */}
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(45,212,176,0.15)', border: '2px solid ' + TEAL, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={TEAL} strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>

          <div style={{ fontSize: '20px', fontWeight: 800, color: '#fff', marginBottom: '8px' }}>Order request submitted!</div>
          <div style={{ fontSize: '13px', color: '#6B7A90', marginBottom: '20px', lineHeight: 1.7 }}>
            Your details have been sent to <strong style={{ color: '#fff' }}>{seller?.shop_name || 'the seller'}</strong>.
            They will review and confirm your order shortly.
          </div>

          {/* What's next */}
          <div style={{ padding: '14px 16px', borderRadius: '12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '13px', color: '#D1D5DB', lineHeight: 1.7, marginBottom: '14px', textAlign: 'left' }}>
            <div style={{ fontWeight: 700, color: '#fff', marginBottom: '8px' }}>What happens next?</div>
            The seller will confirm through their usual channel — Facebook inbox, WhatsApp, or phone call. Please check your messages.
          </div>

          {/* Customer portal */}
          <div style={{ padding: '14px 16px', borderRadius: '12px', background: 'rgba(45,212,176,0.08)', border: '2px solid ' + TEAL, marginBottom: '14px', textAlign: 'left' }}>
            <div style={{ fontWeight: 700, color: TEAL, fontSize: '13px', marginBottom: '6px' }}>Track your order online</div>
            <div style={{ fontSize: '12px', color: '#D1D5DB', marginBottom: '10px', lineHeight: 1.6 }}>Check your order status anytime without waiting for a message.</div>
            <a href="/customer/portal"
              style={{ display: 'block', textAlign: 'center', padding: '11px', borderRadius: '10px', background: TEAL, color: NAVY, fontSize: '13px', fontWeight: 700, textDecoration: 'none', marginBottom: '8px' }}>
              Go to customer portal →
            </a>
            <div style={{ fontSize: '12px', color: '#6B7A90', textAlign: 'center' }}>
              Not registered?{' '}
              <a href="/customer/signup" style={{ color: TEAL, textDecoration: 'none', fontWeight: 600 }}>Sign up free</a>
              {' '}to track orders
            </div>
          </div>

          {/* WhatsApp */}
          {waLink && (
            <a href={waLink} target="_blank" rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '13px', borderRadius: '12px', background: '#25D366', fontSize: '14px', fontWeight: 700, color: '#fff', textDecoration: 'none', marginBottom: '14px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Message {seller?.shop_name || 'seller'} on WhatsApp
            </a>
          )}

          <div style={{ fontSize: '11.5px', color: '#4B5563' }}>
            Please do not resubmit — duplicates are ignored.<br/>
            Powered by PageConnect · pageconnect.io
          </div>
        </div>
        <style>{`@keyframes cof-spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    )
  }

  // ── MAIN FORM ──
  return (
    <div style={{ minHeight: '100vh', background: WARM, paddingBottom: '100px', fontFamily: 'Space Grotesk, -apple-system, sans-serif' }}>

      {/* ── Sticky header ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: NAVY, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {seller?.logo_light_url
            ? <img src={seller.logo_light_url} alt={seller?.shop_name} style={{ height: '28px', objectFit: 'contain' }} />
            : <div style={{ fontSize: '15px', fontWeight: 700, color: '#fff' }}>{seller?.shop_name || 'Order Form'}</div>
          }
        </div>
        {/* Timer pill */}
        {timerStarted && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 10px', borderRadius: '20px', background: TC.bg, border: '1px solid ' + TC.border }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={TC.text} strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span style={{ fontSize: '13px', fontWeight: 700, color: TC.text, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.05em' }}>
              {String(mins).padStart(2,'0')}:{String(secs).padStart(2,'0')}
            </span>
          </div>
        )}
      </div>

      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '16px 16px 0' }}>

        {/* ── Product preview ── */}
        {(request?.product_name || request?.product_image_url || request?.product_code) && (
          <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', overflow: 'hidden', marginBottom: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px' }}>
            {request.product_image_url && (
              <img src={request.product_image_url} alt={request.product_name || 'Product'} style={{ width: '60px', height: '60px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }} />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '3px' }}>You are ordering</div>
              {request.product_name && <div style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A', marginBottom: '2px' }}>{request.product_name}</div>}
              {request.product_code && <div style={{ fontSize: '12px', fontFamily: 'JetBrains Mono, monospace', color: TEAL, fontWeight: 600 }}>{request.product_code}</div>}
              {request.product_price && <div style={{ fontSize: '16px', fontWeight: 800, color: NAVY, marginTop: '4px' }}>{request.currency_sym || '৳'}{Number(request.product_price).toLocaleString()}</div>}
            </div>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: TEAL, flexShrink: 0 }} />
          </div>
        )}

        {/* ── How it works — compact 3-step strip ── */}
        <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #E5E7EB', padding: '12px 16px', marginBottom: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
            {[
              { ic: '📝', label: 'Fill in form' },
              { ic: '→',  label: '', arrow: true },
              { ic: '✓',  label: 'Submit', teal: true },
              { ic: '→',  label: '', arrow: true },
              { ic: '📞', label: 'Seller confirms' },
            ].map((s, i) => s.arrow
              ? <div key={i} style={{ color: '#D1D5DB', fontSize: '14px', fontWeight: 600 }}>›</div>
              : <div key={i} style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: '18px', marginBottom: '2px' }}>{s.teal ? <span style={{ color: TEAL, fontWeight: 800, fontSize: '16px' }}>✓</span> : s.ic}</div>
                  <div style={{ fontSize: '10.5px', color: '#6B7280', fontWeight: 600 }}>{s.label}</div>
                </div>
            )}
          </div>
          <div style={{ marginTop: '10px', padding: '8px 12px', borderRadius: '8px', background: '#FFF7ED', border: '1px solid #FDE68A', fontSize: '12px', color: '#92400E', lineHeight: 1.5 }}>
            ⚠️ Your order is <strong>NOT confirmed</strong> until the seller approves it. They will contact you to confirm.
          </div>
        </div>

        {/* ── Your details ── */}
        <div style={{ marginBottom: '12px' }}>
          <SCard title="Your details" accent={NAVY}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <Lbl>Full name <span style={{ color: '#EF4444' }}>*</span></Lbl>
                <input type="text" value={form.customer_name} onChange={e => setF('customer_name', e.target.value)}
                  placeholder="Your full name" style={iStyle} onFocus={iF} onBlur={iB} />
              </div>
              <div>
                <PhoneInput
                  label="Phone number *"
                  countryCode={form.country_code} phone={form.phone}
                  onCountryCodeChange={v => setF('country_code', v)}
                  onPhoneChange={v => setF('phone', v)}
                  placeholder={isInt ? '412345678' : '1712345678'}
                  required />
                <div style={{ marginTop: '6px', padding: '8px 12px', borderRadius: '8px', background: '#F8FAFC', border: '1px solid #E2E8F0', fontSize: '12px', color: '#9CA3AF', lineHeight: 1.5 }}>
                  Used for order tracking and confirmation only. The seller will contact you on this number.
                </div>
              </div>
            </div>
          </SCard>
        </div>

        {/* ── Product code ── */}
        <div style={{ marginBottom: '12px' }}>
          <SCard title="Product" accent={TEAL}>
            <Lbl optional hint="The seller may have shared a product code in their post or message — enter it here if you have one">
              Product code
            </Lbl>
            <input type="text" value={form.product_code} onChange={e => setF('product_code', e.target.value)}
              placeholder="e.g. PC-001 — leave blank if not sure" style={iStyle} onFocus={iF} onBlur={iB} />
          </SCard>
        </div>

        {/* ── Delivery address ── */}
        <div style={{ marginBottom: '12px', borderRadius: '16px', overflow: 'hidden', border: '2px solid ' + AMBER, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {/* Amber header */}
          <div style={{ padding: '12px 18px', background: AMBER }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>🚚 Delivery address</div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.85)', marginTop: '2px' }}>Fill every field carefully — incorrect address = failed delivery</div>
          </div>

          {/* Warning banner */}
          <div style={{ padding: '10px 18px', background: '#FFFBEB', borderBottom: '1px solid #FDE68A' }}>
            <div style={{ fontSize: '12.5px', color: '#92400E', lineHeight: 1.6 }}>
              Select <strong>N/A</strong> for fields that don't apply. Double-check the courier address preview before submitting — you cannot change it after.
            </div>
          </div>

          {/* Address fields */}
          <div style={{ padding: '16px 18px', background: '#fff' }}>
            <BDAddressFields address={form} onChange={setF} isBD={!isInt} />
          </div>

          {/* Courier preview */}
          {(form.house || form.district || form.metro_thana) && (
            <div style={{ padding: '0 18px 16px', background: '#fff' }}>
              <div style={{ padding: '12px 14px', borderRadius: '12px', border: '2px solid ' + TEAL, background: TEAL + '0D' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: NAVY, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '6px' }}>
                  📦 Your parcel will be addressed to
                </div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: NAVY, lineHeight: 1.6, marginBottom: '8px' }}>
                  {courierPreview() || 'Fill in address fields above'}
                </div>
                <div style={{ fontSize: '12px', color: '#DC2626', lineHeight: 1.5 }}>
                  If anything looks wrong — correct it above before submitting.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Notes ── */}
        <div style={{ marginBottom: '12px' }}>
          <SCard title="Special requests" accent="#6B7280">
            <Lbl optional hint="Preferred colour, size, delivery time, gift wrapping, any special instructions for the seller">
              Notes or special requests
            </Lbl>
            <textarea value={form.notes} onChange={e => setF('notes', e.target.value)}
              placeholder="e.g. preferred colour, size, delivery instructions..."
              rows={3} style={{ ...iStyle, resize: 'none' }} onFocus={iF} onBlur={iB} />
          </SCard>
        </div>

        {/* ── Optional profile — reframed as benefit ── */}
        <div style={{ marginBottom: '12px', background: '#fff', borderRadius: '16px', border: '1px solid #E5E7EB', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          {/* Toggle header */}
          <button onClick={() => setShowProfile(o => !o)} style={{ width: '100%', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#0F172A' }}>A little about you</span>
                <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '1px 7px', borderRadius: '20px', background: '#F0FDF4', color: '#16A34A' }}>Optional</span>
                {filledProfile > 0 && <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '1px 7px', borderRadius: '20px', background: TEAL + '20', color: TEAL_D }}>{filledProfile}/8 filled</span>}
              </div>
              <div style={{ fontSize: '12.5px', color: '#6B7280', lineHeight: 1.5 }}>
                Help <strong style={{ color: '#374151' }}>{seller?.shop_name || 'the seller'}</strong> personalise offers, birthday/anniversary surprises, and communicate better — all optional, entirely your choice.
              </div>
            </div>
            <div style={{ flexShrink: 0, marginTop: '2px' }}>
              <div style={{ width: '32px', height: '18px', borderRadius: '9px', background: showProfile ? TEAL : '#D1D5DB', position: 'relative', transition: 'background 0.2s' }}>
                <div style={{ position: 'absolute', top: '2px', left: showProfile ? '16px' : '2px', width: '14px', height: '14px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
              </div>
            </div>
          </button>

          {showProfile && (
            <div style={{ padding: '0 18px 18px', borderTop: '1px solid #F3F4F6' }}>
              {/* Encouraging banner */}
              <div style={{ margin: '14px 0 16px', padding: '12px 14px', borderRadius: '10px', background: 'linear-gradient(135deg, ' + TEAL + '15, ' + TEAL + '05)', border: '1px solid ' + TEAL + '30' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: NAVY, marginBottom: '4px' }}>
                  🎁 Why this helps you
                </div>
                <div style={{ fontSize: '12.5px', color: '#374151', lineHeight: 1.6 }}>
                  Sellers who know their customers better send more relevant offers, remember your preferences, and can reach you through the right channel. None of this is required — but the more you share, the better your experience.
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <Lbl optional hint="So we can send you a birthday surprise 🎂">Date of birth</Lbl>
                  <input type="date" value={form.birthday} onChange={e => setF('birthday', e.target.value)} style={iStyle} onFocus={iF} onBlur={iB} />
                </div>
                <div>
                  <Lbl optional hint="So we can wish you on your anniversary 💍">Anniversary date</Lbl>
                  <input type="date" value={form.anniversary} onChange={e => setF('anniversary', e.target.value)} style={iStyle} onFocus={iF} onBlur={iB} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div>
                  <Lbl optional hint="Helps tailor product recommendations">Gender</Lbl>
                  <select value={form.gender} onChange={e => setF('gender', e.target.value)} style={iStyle} onFocus={iF} onBlur={iB}>
                    {GENDER_OPTIONS.map(g => <option key={g} value={g === 'Prefer not to say' ? '' : g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <Lbl optional hint="Helps the seller pick offers that suit you">Age group</Lbl>
                  <select value={form.age_range} onChange={e => setF('age_range', e.target.value)} style={iStyle} onFocus={iF} onBlur={iB}>
                    {AGE_GROUPS.map(a => <option key={a} value={a === 'Prefer not to say' ? '' : a}>{a}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div>
                  <Lbl optional hint="If different from your phone number above">WhatsApp</Lbl>
                  <input type="text" value={form.whatsapp} onChange={e => setF('whatsapp', e.target.value)} placeholder="e.g. 01712345678" style={iStyle} onFocus={iF} onBlur={iB} />
                </div>
                <div>
                  <Lbl optional hint="For order receipts or updates">Email address</Lbl>
                  <input type="email" value={form.email} onChange={e => setF('email', e.target.value)} placeholder="you@example.com" style={iStyle} onFocus={iF} onBlur={iB} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div>
                  <Lbl optional hint="So the seller can follow or tag you">Facebook profile</Lbl>
                  <input type="text" value={form.facebook_url} onChange={e => setF('facebook_url', e.target.value)} placeholder="facebook.com/..." style={iStyle} onFocus={iF} onBlur={iB} />
                </div>
                <div>
                  <Lbl optional>Instagram</Lbl>
                  <input type="text" value={form.instagram_url} onChange={e => setF('instagram_url', e.target.value)} placeholder="instagram.com/..." style={iStyle} onFocus={iF} onBlur={iB} />
                </div>
              </div>

              {/* Completion encouragement */}
              {filledProfile < 4 && (
                <div style={{ marginTop: '14px', padding: '10px 14px', borderRadius: '10px', background: '#F0FDF4', border: '1px solid #BBF7D0', fontSize: '12.5px', color: '#166534', lineHeight: 1.5 }}>
                  ✨ You've filled {filledProfile} of 8 optional fields. The more you add, the more personalised your experience with <strong>{seller?.shop_name || 'this seller'}</strong>.
                </div>
              )}
              {filledProfile >= 4 && (
                <div style={{ marginTop: '14px', padding: '10px 14px', borderRadius: '10px', background: TEAL + '15', border: '1px solid ' + TEAL + '40', fontSize: '12.5px', color: TEAL_D, lineHeight: 1.5 }}>
                  🌟 Great job! {filledProfile}/8 fields filled — {seller?.shop_name || 'the seller'} will be able to serve you much better.
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Error ── */}
        {error && (
          <div style={{ marginBottom: '12px', padding: '12px 14px', borderRadius: '12px', background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '13px', color: '#DC2626', lineHeight: 1.5 }}>
            {error}
          </div>
        )}
      </div>

      {/* ── Sticky submit bar ── */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '12px 16px', background: '#fff', borderTop: '1px solid #E5E7EB', boxShadow: '0 -4px 20px rgba(0,0,0,0.08)', zIndex: 20 }}>
        <div style={{ maxWidth: '560px', margin: '0 auto' }}>
          <button onClick={handleSubmit} disabled={submitting || expired}
            style={{ width: '100%', padding: '15px', borderRadius: '14px', background: submitting || expired ? '#D1D5DB' : TEAL, border: 'none', fontSize: '16px', fontWeight: 700, color: submitting || expired ? '#9CA3AF' : NAVY, cursor: submitting || expired ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background 0.15s', fontFamily: 'inherit' }}>
            {submitting
              ? <><div style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2.5px solid rgba(0,0,0,0.2)', borderTopColor: NAVY, animation: 'cof-spin 0.7s linear infinite' }} />Submitting...</>
              : <>Submit order request <span style={{ fontSize: '18px' }}>→</span></>
            }
          </button>
          <div style={{ textAlign: 'center', fontSize: '11.5px', color: '#9CA3AF', marginTop: '6px' }}>
            Order NOT confirmed until seller approves it
          </div>
        </div>
      </div>

      <style>{`
        @keyframes cof-spin { to { transform: rotate(360deg) } }
        @media(max-width:480px) {
          [data-profile-grid] { grid-template-columns: 1fr !important; }
        }
        * { -webkit-tap-highlight-color: transparent; }
        input, select, textarea { font-size: 16px !important; }
      `}</style>
    </div>
  )
}

export default CustomerOrderForm